// src/pages/CategoriesList.js
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL;

export default function CategoriesList() {
  const { data: categories, isLoading, error } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/servicecategory/`);
      return res.data;
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white">
        Loading categories...
      </div>
    );
  }

  if (error) {
    toast.error('Failed to load categories');
    return (
      <div className="min-h-screen flex items-center justify-center text-red-400">
        Error loading categories
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-x-hidden">
      {/* Background blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <Helmet>
        <title>Service Categories in Kenya | Find Professionals on SErraND</title>
        <meta
          name="description"
          content="Browse professional service categories on SErraND including mechanics, beauty and salon, plumbing, electrical, cleaning, and more across Kenya."
        />
      </Helmet>

      {/* Main content */}
      <section className="relative py-16 px-4 max-w-7xl mx-auto z-10">
        <h1 className="text-5xl md:text-7xl font-bold text-white text-center mb-6">
          Service Categories
        </h1>

        <p className="text-xl text-gray-300 text-center max-w-3xl mx-auto mb-12">
          Find trusted and verified service providers across Kenya by browsing
          professional categories tailored to your needs.
        </p>

        {/* SEO intro block */}
        <section className="max-w-4xl mx-auto text-center mb-16">
          <p className="text-gray-300 text-lg leading-relaxed">
            SErraND connects individuals and businesses with skilled professionals
            across a wide range of service categories. From vehicle repairs and
            home maintenance to beauty, electrical, and technical services, each
            category lists experienced providers available in your area, helping
            you hire with confidence.
          </p>
        </section>

        {/* Category grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {categories?.map((cat) => (
            <Link
              key={cat.slug}
              to={`/categories/${cat.slug}`}
              className="group bg-gray-800/60 backdrop-blur-lg border border-gray-700 rounded-2xl overflow-hidden hover:border-purple-500 transition-all hover:shadow-2xl hover:scale-[1.02]"
            >
              {cat.image && (
                <img
                  src={cat.image}
                  alt={`${cat.name} services in Kenya`}
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform"
                />
              )}

              <div className="p-6">
                <h2 className="text-2xl font-bold text-white mb-2 group-hover:text-purple-400 transition-colors">
                  {cat.name}
                </h2>

                <p className="text-gray-300 mb-4 line-clamp-3">
                  {cat.description ||
                    'Browse professional service providers in this category.'}
                </p>

                <div className="flex items-center justify-between">
                  <span className="text-purple-400 font-medium">
                    {cat.provider_count} Providers
                  </span>
                  <span className="text-gray-400 group-hover:text-white transition-colors">
                    Explore →
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Supporting SEO content */}
        <section className="max-w-5xl mx-auto mt-20 text-gray-300">
          <h2 className="text-3xl font-bold text-white mb-6 text-center">
            Browse Professional Services by Category
          </h2>

          <p className="leading-relaxed mb-4">
            SErraND organizes service providers into clear and searchable
            categories, making it easy to find the right professional for any
            task. Each category page provides detailed information about the
            service, available providers, and locations served.
          </p>

          <p className="leading-relaxed">
            By browsing categories, you can compare providers, view verified
            profiles, and choose professionals based on experience, pricing, and
            customer feedback before making a hiring decision.
          </p>
        </section>
      </section>
    </div>
  );
}
